import {SlctFormat, SlctInternalProps, SlctLabelOnlyProps, SlctOption} from 'module/Slct/type/SlctType'
import React, {FocusEventHandler, KeyboardEvent, useCallback, useEffect, useMemo} from 'react'
import {CircularProgress} from '@mui/material'
import {log} from 'common/log'
import {Key} from 'util/Key'
import {Clear} from '@mui/icons-material'
import {useDispatch, useSelector} from 'react-redux'
import {selMemoMap} from 'module/Memo/selector/MemoSelector'
import {cssCFInputBorder, cssCFInputBorderFocussed} from 'module/CustomField/style/cssCFInput'
import {memoAddAction} from 'module/Memo/action/MemoAction'
import {getOptionLabel} from 'module/Slct/util/getOptionLabel'
import {
	cssSlctChildBase,
	cssSlctClearButton,
	cssSlctEllipsis,
	cssSlctInput,
	cssSlctInputLoadingWrapper,
	cssSlctPlaceholder,
	cssSlctPlaceholderText,
	cssSlctSingleChild,
	cssSlctWrapper,
} from 'module/Slct/style/SlctStyle'
import {useSlctOptions} from 'module/Slct/hook/useSlctOptions'
import {getNextCursor} from 'module/Slct/util/getNextCursor'
import {isMemorizable} from 'module/Memo/type/Memorizable'
import {focusNextFormElement} from 'util/view/focusNextFormElement'
import {SetEvent} from 'type/SetCb'
import {useSlctProps} from 'module/Slct/hook/useSlctProps'
import {useSlctChangeState} from 'module/Slct/hook/useSlctChangeState'
import {useSlctSharedCallbacks} from 'module/Slct/hook/useSlctSharedCallbacks'
import {SlctMenuComponent} from 'module/Slct/component/SlctMenuComponent'
import {css} from '@emotion/react'
import {getTextAlignCSS} from 'module/CustomField/style/CFAlignmentStyle'
import {getSlctColor} from 'module/Slct/util/GetSlctColor'

/* use memo functions to pass props to this component */
export const SlctLabelOnly = React.memo(function SlctLabelOnly<ModelType extends object | void, ValueType extends string | number>(props: SlctLabelOnlyProps<ModelType, ValueType>) {
	const dispatch = useDispatch()
	let {
		value, onChange: _onChange,
		options: optionsUnfiltered,
		filterOptions,
		onChangeState,
		preventCloseMenuOnSelect, menuWidth, menuHeight, hideClearButton,
		disabled, loading,
		placeholderHTML = '', autoFocus, tabIndex = 0, align,
		menuPortal,
	} = props

	const displayFormat = SlctFormat.Label

	const {
		byValue,
		inputValue, setInputValue,
		cursor, setCursor,
		focus, setFocus,
		menuOpen, setMenuOpen,
		memoKey, memoSize, memoAutoAdd,
		hasMenu,
		ref,
		refInput, setRefInput,
		refMenu,
		refList,
	} = useSlctProps(props)

	const memoOpts = (useSelector(selMemoMap)?.[memoKey || ''] || []) as SlctOption<ModelType, ValueType>[]

	const onChange = useCallback((o: SlctOption<ModelType, ValueType> | null, event?: SetEvent) => {
		log.trace('Slct onChange', o)
		if (disabled || o?.disabled) return;
		if (!preventCloseMenuOnSelect) {
			setMenuOpen(false)
			setCursor(null)
			setInputValue('')
			if (event) focusNextFormElement(1, {inputEl: refInput.current, event, focusNextOnlyInForm: true})
		}
		_onChange(o)
		if (memoKey && memoSize && memoAutoAdd && isMemorizable(o)) {
			dispatch(memoAddAction({memoKey, memoSize, element: o}))
		}
	}, [memoKey, memoSize, memoAutoAdd, refInput.current, disabled, preventCloseMenuOnSelect, setMenuOpen, setCursor, _onChange])

	const valueObj: SlctOption<ModelType, ValueType> | null = useMemo(() => {
		if (value || value === 0) {
			return {
				...(byValue?.[value] || {value}),
				memo: !!memoOpts.find(m => m.value === value),
				active: true,
			} as SlctOption<ModelType, ValueType>
		} else {
			return null
		}
	}, [value, byValue])

	const {options, valueIndex} = useSlctOptions({
		memoKey,
		optionsUnfiltered,
		filterOptions,
		value,
		valueObj,
		inputValue,
		byValue,
		filterOnInputValueEqualsValue: false,
	})
	const maxIndex = options.length - 1

	useSlctChangeState(onChangeState, inputValue, focus)

	const {
		onClickInput,
		onChangeInput,
		onKeyUp,
	} = useSlctSharedCallbacks(setMenuOpen, setInputValue, refInput)

	const onFocus: FocusEventHandler<HTMLDivElement> = useCallback((evt) => {
		log.trace('Slct onInputFocus', evt)
		setFocus(true)
		setMenuOpen(true)
		setCursor(null)
	}, [setFocus, setMenuOpen, setCursor])

	const onBlur: FocusEventHandler<HTMLElement> = useCallback((evt) => {
		const focusedMenu = refMenu.current?.contains(evt.relatedTarget)
		const focusedInput = refInput.current?.contains(evt.relatedTarget)
		if (focusedMenu || focusedInput) {
			log.trace('Slct onBlur cancel', evt)
			setMenuOpen(true)
			setFocus(true)
			evt.preventDefault()
			evt.stopPropagation()
			refInput.current?.focus()
			return;
		}
		log.trace('Slct onBlur blur', evt)
		// clicked / tabbed outside
		setMenuOpen(false)
		setCursor(null)
		setFocus(false)
		setInputValue('')
	}, [refInput.current, refMenu.current, setFocus, setMenuOpen, setCursor])

	const onClear = useCallback(() => {
		log.trace('Slct onClear')
		onChange(null)
		refInput.current?.focus()
	}, [onChange, refInput.current])

	const onKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
		log.trace('Slct onKeyDown', event)
		if (cursor !== null && (event.key === Key.Tab || event.key === Key.Enter)) {
			const o = options[cursor]
			if (o && !o.active) {
				onChange(o, event)
			}
			return;
		} else if (event.key === Key.Escape) {
			setInputValue('')
			refInput.current?.blur()
			return;
		}
		if (event.ctrlKey || event.altKey || event.shiftKey) return;
		const nextCursor = getNextCursor(event, cursor === null && valueIndex > -1 ? valueIndex : cursor, maxIndex)
		if (nextCursor !== null) refList.current?.scrollToItem(nextCursor, "smart")
		setCursor(nextCursor)
		log.debug('Slct set cursor / scroll to item', inputValue, cursor, valueIndex)
	}, [refInput.current, refList.current, onChange, maxIndex, cursor, options, valueIndex, setCursor, setInputValue])

	const itemData: SlctInternalProps<ModelType, ValueType> = {
		...props,
		// new, internal props
		inputValue,
		focus,
		cursor, setCursor,
		//overridden before
		options,
		displayFormat,
		onClickOption: useCallback((o, event) => {
				log.trace('Slct option clicked', o)
				onChange(o, event)
			}, [onChange],
		),
		refInput,
	}

	const onClickWrapper = useCallback(() => refInput.current?.focus(), [refInput.current])

	const displayValue = getOptionLabel(SlctFormat.Label, valueObj)

	useEffect(() => {
		if (!(menuOpen && valueIndex > -1 && !inputValue)) return
		// menu opened, scroll to current value
		refList.current?.scrollToItem(valueIndex, "start")
		log.debug('Slct scroll to item', menuOpen, valueIndex)
	}, [refList.current, menuOpen, inputValue, valueIndex])

	let {background, color} = getSlctColor({disabled, valueObj, value, focus})
	return <>
		<div
			className={'SlctWrapper'}
			data-cursor-key-needs-modifier={hasMenu}
			css={[
				cssSlctWrapper,
				css`
					background: ${background};
					color: ${color};
				`,
				focus ? cssCFInputBorderFocussed : cssCFInputBorder,
			]}
			onFocus={onFocus}
			onClick={onClickWrapper}
			ref={ref}
		>
			<input
				className={'SlctInput'}
				css={[
					cssSlctChildBase,
					cssSlctSingleChild,
					cssSlctInput,
					getTextAlignCSS(align),
				]}
				ref={setRefInput}
				autoFocus={autoFocus}
				tabIndex={tabIndex}
				value={focus ? inputValue : ''}
				onChange={onChangeInput}
				onClick={onClickInput}
				onBlur={onBlur}
				onKeyUp={onKeyUp}
				onKeyDown={onKeyDown}
				disabled={disabled}
			/>
			{(!focus || !inputValue) && <div
				className={'SlctPlaceholder'}
				css={[
					cssSlctChildBase,
					cssSlctSingleChild,
					cssSlctPlaceholder,
					cssSlctEllipsis,
					getTextAlignCSS(align),
				]}
			>
				{!value && value !== 0 ?
					<div css={[cssSlctPlaceholderText, cssSlctEllipsis]} dangerouslySetInnerHTML={{__html: placeholderHTML || ''}}/>
					:
					<div css={[focus ? cssSlctPlaceholderText : undefined]}>{displayValue}</div>
				}
			</div>
			}
			{loading &&
				<div className={'SlctLoading'} css={cssSlctInputLoadingWrapper}>
					<CircularProgress size={'inherit'} color={'inherit'}/>
				</div>
			}
			{!hideClearButton && (value || value === 0) && !disabled &&
				<div
					css={cssSlctClearButton}
					onClick={onClear}
				>
					<Clear fontSize={'inherit'}/>
				</div>
			}
		</div>
		<SlctMenuComponent
			menuOpen={menuOpen}
			loading={loading}
			tabIndex={tabIndex}
			itemData={itemData}
			menuWidth={menuWidth}
			menuHeight={menuHeight}
			refWrapper={ref}
			refInput={refInput}
			refMenu={refMenu}
			refList={refList}
			menuPortal={menuPortal}
		/>
	</>
})